import * as React from 'react';
import { Component, Fragment } from 'react';
import { observer } from 'mobx-react';
import { computed } from 'mobx';
import { withTheme } from 'styled-components';
import { styled } from '../utils/styled';
import { propStyles } from '../utils/propStyles';
import { textOverflow, whiteSpace } from '../utils/customPropStyles';
import getQueryResults from '../utils/query/getQueryResults';
import ShareclassColumnValue from './ShareclassColumnValue';
import ShareclassRowExpander from './ShareclassRowExpander';
import Tooltip from './Tooltip';
import { camelCase } from '@kurtosys/ksys-app-template/dist/utils/changeCase';
import { EntityStore, DataStore, EntityGroupStore } from '../stores';
import { ColumnConfig } from '../models/configuration/explorer/ColumnConfig';
import { inject } from '../utils';
import { ShareClass } from '../models/entity';
import { Theme } from '../models/Theme';

const ShareclassRow = styled<any, any>('tr', 'shareclassRow', 'shareclassRows')`
    ${propStyles};
`;

ShareclassRow.defaultProps = {
    cursor: 'pointer',
    bgColor: 'white',
    borderWidth: '1px',
    borderColor: 'gray300',
    borderStyle: 'none none solid none',
    alternated: {
        bgColor: 'gray100',
    },
    hover: {
        bgColor: 'gray200',
    },
};

export const ShareclassColumn = styled<any, any>(
    'td',
    'shareclassColumn',
    'shareclassRows'
)`
    ${propStyles};
    ${textOverflow};
    ${whiteSpace};
`;

ShareclassColumn.defaultProps = {
    paddingX: 'sm',
    paddingY: 'md',
    fontSize: 'sm',
    overflow: ' hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
};

interface ShareclassRowsProps {
    theme?: Theme;
    /** Injected by withShareclass */
    dataStore?: DataStore;
    entityGroupStore?: EntityGroupStore;
    /** */
    entityStore?: EntityStore;
    columns: Array<ColumnConfig>;
    alternated: boolean;
    shareClass?: ShareClass;
    rowAccessibileContext: string;
    rowIndex: number;
}

@inject('entityStore')
@observer
class ShareclassRows extends Component<ShareclassRowsProps, any> {
    @computed
    get shareclassRowTheme() {
        const { theme } = this.props;
        return this.rowExpandable
            ? theme.ShareclassRow
            : { ...theme.ShareclassRow, ...theme.ShareclassRowDisabled };
    }

    @computed
    get shareclassColumnTheme() {
        const { theme } = this.props;
        return this.rowExpandable
            ? theme.ShareclassColumn
            : { ...theme.ShareclassColumn, ...theme.ShareclassColumnDisabled };
    }

    @computed
    get checkEnableByFundList() {
        const { dataStore, entityStore, entityGroupStore } = this.props;
        const fundListCheck = entityGroupStore.rowExpanderConfig?.fundListCheck;
        if (fundListCheck && entityStore) {
            return dataStore.fundListContainsEntity(
                entityStore.value as any,
                fundListCheck
            );
        }
        return true;
    }

    @computed
    get rowExpandable() {
        const { entityStore } = this.props;
        if (
            !this.checkEnableByFundList ||
            entityStore?.isShareclassDisabled ||
            entityStore?.disableRow
        ) {
            return false;
        }
        return true;
    }

    @computed
    get disabledTooltipDisclaimer() {
        const { dataStore, entityStore, entityGroupStore } = this.props;
        if (entityGroupStore.rowExpanderConfig?.disabledTooltipDisclaimer) {
            const disclaimerQuery = {
                disclaimer:
                    entityGroupStore.rowExpanderConfig
                        .disabledTooltipDisclaimer,
            };
            const data = getQueryResults(
                entityStore.queryContext,
                disclaimerQuery,
                dataStore.culture
            );
            if (data && data.disclaimer) {
                return data.disclaimer;
            }
        }
        return;
    }

    renderRowExpander(shareClassProp) {
        const { theme, rowAccessibileContext } = this.props;
        if (this.rowExpandable) {
            return <ShareclassRowExpander labelledby={rowAccessibileContext} />;
        } else {
            const disabledTooltip = this.disabledTooltipDisclaimer;
            if (disabledTooltip) {
                return (
                    <ShareclassColumn
                        textAlign="center"
                        {...theme.ShareclassColumn}
                    >
                        <Tooltip
                            content={[disabledTooltip]}
                            accessibilityContext={`for disabled share class identified by ${shareClassProp.shareClass.clientCode}`}
                            analyticsContext={{
                                metricContext: 'shareclassRows-disabled',
                                source: 'ShareclassColumn',
                                // Disabled tooltip disclaimer is queried, so no disclaimer type is available to set, defaulting to: disabled_tooltip
                                tooltipCode: 'disabled_tooltip',
                            }}
                        />
                    </ShareclassColumn>
                );
            }
        }
        return <ShareclassColumn {...theme.ShareclassColumn} />;
    }

    @computed
    get totalColumns(): number {
        const { columns, dataStore } = this.props;
        if (columns) {
            let count = (columns as Array<any>)
                .map((column, idx) => {
                    if (column.groups) {
                        // get the selected group column count
                        let index = dataStore.columnStore.selectedGroupMap.get(
                            idx
                        );
                        if (index >= column.groups.length) {
                            const selectedConfigGroupIndex = column.groups.findIndex(
                                c => c.selected
                            );
                            index =
                                selectedConfigGroupIndex > -1
                                    ? selectedConfigGroupIndex
                                    : 0;
                        }
                        const selectedGroup = column.groups[index];
                        const groupedColumnTotal =
                            selectedGroup &&
                            selectedGroup.columns &&
                            selectedGroup.columns.length;
                        return groupedColumnTotal;
                    }
                    return column.total;
                })
                .reduce((total, num) => {
                    return total + num;
                });
            if (this.rowExpandable) {
                count++;
            }
            return count;
        }
    }

    render() {
        const {
            theme,
            entityStore,
            entityGroupStore,
            alternated,
            children,
            dataStore,
            rowIndex,
            ...shareClass
        } = this.props;

        return (
            <Fragment>
                <ShareclassRow
                    data-alternated={alternated}
                    data-selected={
                        entityGroupStore.rowHasExpandableOptions &&
                        entityStore?.expanded &&
                        this.rowExpandable
                    }
                    onClick={
                        entityGroupStore.rowHasExpandableOptions &&
                        this.rowExpandable
                            ? entityStore?.toggleExpanded
                            : () => null
                    }
                    cursor={
                        entityGroupStore.rowHasExpandableOptions &&
                        this.rowExpandable
                            ? 'pointer'
                            : 'auto'
                    }
                    data-metric-id={`shareclassRow-expand-toggle-${camelCase(
                        entityStore?.value?.clientCode || ''
                    )}`}
                    {...this.shareclassRowTheme}
                    aria-rowindex={rowIndex}
                >
                    {entityGroupStore.rowHasExpandableOptions &&
                        (!entityGroupStore.rowExpanderConfig ||
                            entityGroupStore.rowExpanderConfig.align ===
                                'left') &&
                        this.renderRowExpander(shareClass)}
                    {shareClass.columns.map((column, index) => {
                        const shareClassColumnValue = (
                            <ShareclassColumnValue
                                key={`value-${column.type}-${index}`}
                                column={column}
                                columnIndex={index}
                            />
                        );
                        if (['group', 'dropdown'].includes(column.type)) {
                            // Don't wrap in column since the ShareclassColumnValue will output columns for the grouped records
                            return shareClassColumnValue;
                        } else {
                            const shareClassColumnProps = {
                                key: `${column.type}-${index}`,
                                textAlign: column.textAlign || 'center',
                                width: column.width,
                                overflow:
                                    column.type === 'document' ||
                                    column.wrapText
                                        ? 'visible'
                                        : 'hidden',
                                whiteSpace: column.wrapText
                                    ? 'normal'
                                    : 'nowrap',
                                ...this.shareclassColumnTheme,
                            };

                            return (
                                <ShareclassColumn {...shareClassColumnProps}>
                                    {shareClassColumnValue}
                                </ShareclassColumn>
                            );
                        }
                    })}
                    {entityGroupStore.rowHasExpandableOptions &&
                        entityGroupStore.rowExpanderConfig?.align === 'right' &&
                        this.renderRowExpander(shareClass)}
                </ShareclassRow>
                {entityGroupStore.rowHasExpandableOptions &&
                    entityStore?.expanded &&
                    this.rowExpandable && (
                        <tr>
                            <td colSpan={this.totalColumns}>{children}</td>
                        </tr>
                    )}
            </Fragment>
        );
    }
}

export default withTheme(ShareclassRows) as any;



// WEBPACK FOOTER //
// ./src/Components/ShareclassRows.tsx